import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Eye, Info, LayoutTemplate } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/shared/components/page-header';
import { EmptyState } from '@/shared/components/empty-state';
import { ApiError, http } from '@/shared/api/client';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { SegmentedControl } from '@/shared/ui/segmented-control';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Tooltip } from '@/shared/ui/tooltip';
import { BeefreeEditor, type BeefreeSavePayload } from './beefree-editor';
import { EmailPreview } from './email-preview';
import { ApplyToField, type ApplyMode } from './apply-to-field';
import { useEmailMarketing } from '../model/context';
import { useEmailMarketingPaths } from '../model/paths';
import {
  blankBootId,
  BEEFREE_IMPORTER_NOT_CONFIGURED,
  BEEFREE_IMPORTER_NOT_CONFIGURED_MESSAGE,
  BEEFREE_NOT_CONFIGURED,
  parseBeefreeDocument,
  readBeefreeBoot,
  type BeefreeDocument,
} from '../model/beefree-document';
import { blocksToHtml } from '../model/template';
import { marketingErrorToast } from '@/features/marketing-shared';
import type { EmailBlock, EmailTemplate, TemplateCategory, TemplateDraft, TemplateEditor, TemplateStoreRef } from '../model/types';
import type { IToken } from '@beefree.io/react-email-builder';

const CATEGORIES: TemplateCategory[] = ['Promoção', 'Relacionamento', 'Aviso'];
const MAX_TAGS = 5;

type TokenState =
  | { status: 'loading' }
  | { status: 'missing' | 'error'; message: string }
  | { status: 'ready'; token: IToken; uid: string };

type EditorStep = 'editor' | 'details';
type SaveIntent = 'content' | 'advance' | 'confirm' | 'preview';
/** Conversão do HTML de um modelo sem documento Beefree, antes de abrir o editor. */
type ImportState = { status: 'idle' | 'loading' } | { status: 'error'; message: string };

interface SavedSnapshot {
  name: string;
  category: TemplateCategory;
  subject: string;
  previewText: string;
  tags: string;
  domain: string;
  html: string;
  beefreeJson: string;
  /** Ids das lojas da variante (plataforma), ordenados. */
  appliesTo: string;
}

/** Chave estável da lista de lojas, para comparar com o que foi salvo. */
function storesKey(stores?: TemplateStoreRef[]): string {
  return (stores ?? [])
    .map((store) => store.id)
    .sort()
    .join('\0');
}

interface TokenResponse {
  access_token: string;
  v2: boolean;
  uid: string;
}

/** HTML de um modelo que ainda não tem documento Beefree (biblioteca, sistema, blocos antigos). */
function templateSourceHtml(template: Pick<EmailTemplate, 'html' | 'blocks'>): string {
  return template.html.trim() || blocksToHtml(template.blocks);
}

/** Converte HTML em documento Beefree pelo HTML Importer. A chave fica no BFF. */
async function importHtmlDocument(html: string): Promise<{ document: BeefreeDocument } | { message: string }> {
  try {
    const data = await http.post<{ document?: BeefreeDocument }>('/bff/email/beefree-import', { html });
    if (!data.document?.page) return { message: 'O Beefree não devolveu um documento para este HTML.' };
    return { document: data.document };
  } catch (error) {
    const code =
      error instanceof ApiError && error.data && typeof error.data === 'object'
        ? (error.data as { code?: string }).code
        : undefined;
    if (code === BEEFREE_IMPORTER_NOT_CONFIGURED) {
      return { message: error instanceof ApiError ? error.message : BEEFREE_IMPORTER_NOT_CONFIGURED_MESSAGE };
    }
    return { message: error instanceof ApiError ? error.message : 'Não foi possível converter o HTML deste modelo.' };
  }
}

function asToken(data: TokenResponse): IToken | null {
  if (!data.access_token || data.v2 !== true || !data.uid) return null;
  return { access_token: data.access_token, v2: true };
}

async function fetchBeefreeToken(): Promise<{ token: IToken; uid: string } | { message: string; missing: boolean }> {
  try {
    const data = await http.post<TokenResponse>('/bff/email/beefree-token', {});
    const token = asToken(data);
    if (!token) return { message: 'O Beefree não devolveu um token utilizável.', missing: false };
    return { token, uid: data.uid };
  } catch (error) {
    const message = error instanceof ApiError ? error.message : 'Não foi possível abrir o Beefree.';
    const code =
      error instanceof ApiError && error.data && typeof error.data === 'object'
        ? (error.data as { code?: string }).code
        : undefined;
    const missing = code === BEEFREE_NOT_CONFIGURED;
    return { message, missing };
  }
}

function toSnapshot(value: {
  name: string;
  category: TemplateCategory;
  subject: string;
  previewText: string;
  tags: string[];
  domain: string;
  html: string;
  beefreeJson: string;
  appliesTo?: TemplateStoreRef[];
}): SavedSnapshot {
  return {
    appliesTo: storesKey(value.appliesTo),
    name: value.name,
    category: value.category,
    subject: value.subject,
    previewText: value.previewText,
    tags: value.tags.join('\0'),
    domain: value.domain,
    html: value.html,
    beefreeJson: value.beefreeJson,
  };
}

interface DetailsFieldsProps {
  name: string;
  onName: (value: string) => void;
  nameInputRef: RefObject<HTMLInputElement | null>;
  category: TemplateCategory;
  onCategory: (value: TemplateCategory) => void;
  subject: string;
  onSubject: (value: string) => void;
  previewText: string;
  onPreviewText: (value: string) => void;
  tags: string[];
  tagDraft: string;
  onTagDraft: (value: string) => void;
  onAddTag: () => void;
  onRemoveTag: (tag: string) => void;
  domain: string;
  domains: string[];
  onDomain: (value: string) => void;
}

function EmailDetailsFields({
  name,
  onName,
  nameInputRef,
  category,
  onCategory,
  subject,
  onSubject,
  previewText,
  onPreviewText,
  tags,
  tagDraft,
  onTagDraft,
  onAddTag,
  onRemoveTag,
  domain,
  domains,
  onDomain,
}: DetailsFieldsProps) {
  return (
    <Card className="space-y-4 overflow-visible p-4 sm:p-5">
      <div>
        <Label htmlFor="nome-template">Nome</Label>
        <Input
          ref={nameInputRef}
          id="nome-template"
          className="mt-1.5"
          value={name}
          maxLength={50}
          onChange={(event) => onName(event.target.value.slice(0, 50))}
          autoComplete="off"
        />
        <p className="mt-1 text-xs text-muted-foreground">{name.length}/50</p>
      </div>
      <div>
        <p className="text-sm font-medium">Categoria</p>
        <SegmentedControl
          className="mt-2"
          value={category}
          onChange={onCategory}
          options={CATEGORIES.map((item) => ({ value: item, label: item }))}
        />
      </div>
      <div>
        <Label htmlFor="assunto-template">Assunto</Label>
        <Input
          id="assunto-template"
          className="mt-1.5"
          value={subject}
          onChange={(event) => onSubject(event.target.value)}
          autoComplete="off"
        />
      </div>
      <div>
        <Label htmlFor="previa-template">Texto de pré-visualização</Label>
        <Input
          id="previa-template"
          className="mt-1.5"
          value={previewText}
          onChange={(event) => onPreviewText(event.target.value)}
          autoComplete="off"
        />
      </div>
      <div>
        <Label htmlFor="tags-template">Tags</Label>
        <div className="mt-1.5 flex flex-col gap-2 sm:flex-row">
          <Input
            id="tags-template"
            className="min-w-0 flex-1"
            value={tagDraft}
            onChange={(event) => onTagDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                onAddTag();
              }
            }}
            placeholder={tags.length >= MAX_TAGS ? 'Limite de 5 tags' : 'Digite e pressione Enter'}
            disabled={tags.length >= MAX_TAGS}
            autoComplete="off"
          />
          <Button type="button" variant="secondary" onClick={onAddTag} disabled={tags.length >= MAX_TAGS || !tagDraft.trim()}>
            Adicionar
          </Button>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {tags.length}/{MAX_TAGS}
        </p>
        {tags.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-2">
            {tags.map((tag) => (
              <Button key={tag} type="button" variant="secondary" size="sm" onClick={() => onRemoveTag(tag)}>
                {tag} · remover
              </Button>
            ))}
          </div>
        ) : null}
      </div>
      <div>
        <Label htmlFor="dominio-template">Domínio</Label>
        <Select value={domain} onValueChange={onDomain}>
          <SelectTrigger id="dominio-template" className="mt-1.5">
            <SelectValue placeholder="Domínio" />
          </SelectTrigger>
          <SelectContent>
            {domains.map((item) => (
              <SelectItem key={item} value={item}>
                {item}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <p className="text-xs leading-relaxed text-muted-foreground">
        Primeiro nome, nome, loja e pontos entram no Beefree. O descadastro fica no link Cancelar inscrição.
      </p>
    </Card>
  );
}

export function EmailTemplateFormPage() {
  const { templateId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const paths = useEmailMarketingPaths();
  const { templates, templatesState, identities, saveTemplate, scope } = useEmailMarketing();
  const existing = templateId ? templates.find((item) => item.id === templateId) ?? null : null;
  const live = scope.kind === 'passbook';
  // Domínio sugerido para o modelo: o primeiro remetente verificado da loja.
  const defaultDomain = identities.find((item) => item.status === 'verified')?.domain ?? identities[0]?.domain ?? '';
  const editing = Boolean(templateId);
  const platformScope = scope.kind === 'platform';
  const isVariant = Boolean(existing?.variantOf);
  const boot = readBeefreeBoot(location.state);
  const blankId = blankBootId(location.state);
  const bootKey = templateId ?? (boot ? `import-${boot.beefreeJson.length}` : blankId !== null ? `blank-${blankId}` : 'novo');
  const storeName = scope.storeName;

  const [name, setName] = useState((existing?.name ?? '').slice(0, 50));
  const [category, setCategory] = useState<TemplateCategory>(existing?.category ?? 'Promoção');
  const [subject, setSubject] = useState(existing?.subject ?? '');
  const [previewText, setPreviewText] = useState(existing?.previewText ?? '');
  const [blocks, setBlocks] = useState<EmailBlock[]>(existing?.blocks.map((block) => ({ ...block })) ?? []);
  const [html, setHtml] = useState(existing?.html ?? boot?.html ?? '');
  const [plainText, setPlainText] = useState(existing?.plainText ?? '');
  const [beefreeJson, setBeefreeJson] = useState(existing?.beefreeJson ?? boot?.beefreeJson ?? '');
  const [editor, setEditor] = useState<TemplateEditor>(existing?.editor ?? (boot ? 'beefree' : 'drag-drop'));
  const [tags, setTags] = useState<string[]>(existing?.tags.slice(0, MAX_TAGS) ?? []);
  const [tagDraft, setTagDraft] = useState('');
  const [domain, setDomain] = useState(existing?.domain || defaultDomain);
  const [applyMode, setApplyMode] = useState<ApplyMode>(existing?.appliesTo?.length ? 'selected' : 'all');
  const [stores, setStores] = useState<TemplateStoreRef[]>(existing?.appliesTo ?? []);
  const [tokenState, setTokenState] = useState<TokenState>({ status: 'loading' });
  const [importState, setImportState] = useState<ImportState>({ status: 'idle' });
  const [editorEpoch, setEditorEpoch] = useState(0);
  const [step, setStep] = useState<EditorStep>('editor');
  const [persisted, setPersisted] = useState(Boolean(existing));
  const [contentHeld, setContentHeld] = useState(false);
  const [contentDirty, setContentDirty] = useState(false);
  const [busy, setBusy] = useState<SaveIntent | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const loadedKey = useRef<string | null>(null);
  const saveBridge = useRef<(() => Promise<BeefreeSavePayload | null>) | null>(null);
  const latestJson = useRef(beefreeJson);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const persistedId = useRef<string | undefined>(existing?.id);
  const savedRef = useRef(
    toSnapshot({
      name: (existing?.name ?? '').slice(0, 50),
      category: existing?.category ?? 'Promoção',
      subject: existing?.subject ?? '',
      previewText: existing?.previewText ?? '',
      tags: existing?.tags.slice(0, MAX_TAGS) ?? [],
      domain: existing?.domain || defaultDomain,
      html: existing?.html ?? boot?.html ?? '',
      beefreeJson: existing?.beefreeJson ?? boot?.beefreeJson ?? '',
      appliesTo: existing?.appliesTo,
    }),
  );
  const heldRef = useRef({
    html: existing?.html ?? boot?.html ?? '',
    beefreeJson: existing?.beefreeJson ?? boot?.beefreeJson ?? '',
  });
  const delivered = useRef(false);
  const armed = useRef<SaveIntent | null>(null);
  const blockUntil = useRef(0);
  latestJson.current = beefreeJson;

  useEffect(() => {
    if (step !== 'editor') return;
    const timer = window.setTimeout(() => window.dispatchEvent(new Event('resize')), 50);
    return () => window.clearTimeout(timer);
  }, [step, tokenState.status]);

  useEffect(() => {
    if (step !== 'details') return;
    nameInputRef.current?.focus();
  }, [step]);

  useEffect(() => {
    if (loadedKey.current === bootKey) return;
    loadedKey.current = bootKey;
    setStep('editor');
    setContentHeld(false);
    if (templateId && !existing) return;
    const imported = !templateId ? readBeefreeBoot(location.state) : null;
    if (!existing) {
      const nextHtml = imported?.html ?? '';
      const nextJson = imported?.beefreeJson ?? '';
      setName('');
      setCategory('Promoção');
      setSubject('');
      setPreviewText('');
      setBlocks([]);
      setHtml(nextHtml);
      setPlainText('');
      setBeefreeJson(nextJson);
      setEditor(imported ? 'beefree' : 'drag-drop');
      setTags([]);
      setDomain(defaultDomain);
      setTagDraft('');
      setApplyMode('all');
      setStores([]);
      persistedId.current = undefined;
      heldRef.current = { html: nextHtml, beefreeJson: nextJson };
      savedRef.current = toSnapshot({
        name: '',
        category: 'Promoção',
        subject: '',
        previewText: '',
        tags: [],
        domain: defaultDomain,
        html: nextHtml,
        beefreeJson: nextJson,
      });
      setPersisted(false);
      setContentDirty(false);
      return;
    }
    const nextName = existing.name.slice(0, 50);
    const nextTags = existing.tags.slice(0, MAX_TAGS);
    const nextDomain = existing.domain || defaultDomain;
    setName(nextName);
    setCategory(existing.category);
    setSubject(existing.subject);
    setPreviewText(existing.previewText);
    setBlocks(existing.blocks.map((block) => ({ ...block })));
    setHtml(existing.html);
    setPlainText(existing.plainText);
    setBeefreeJson(existing.beefreeJson);
    setEditor(existing.editor);
    setTags(nextTags);
    setDomain(nextDomain);
    setApplyMode(existing.appliesTo?.length ? 'selected' : 'all');
    setStores(existing.appliesTo ?? []);
    persistedId.current = existing.id;
    heldRef.current = { html: existing.html, beefreeJson: existing.beefreeJson };
    savedRef.current = toSnapshot({
      name: nextName,
      category: existing.category,
      subject: existing.subject,
      previewText: existing.previewText,
      tags: nextTags,
      domain: nextDomain,
      html: existing.html,
      beefreeJson: existing.beefreeJson,
      appliesTo: existing.appliesTo,
    });
    setPersisted(true);
    setContentDirty(false);
  }, [bootKey, defaultDomain, existing, location.state, templateId]);

  useEffect(() => {
    let active = true;
    void fetchBeefreeToken().then((result) => {
      if (!active) return;
      if ('token' in result) setTokenState({ status: 'ready', token: result.token, uid: result.uid });
      else setTokenState({ status: result.missing ? 'missing' : 'error', message: result.message });
    });
    return () => {
      active = false;
    };
  }, [bootKey, editorEpoch]);

  // Modelo com HTML mas sem documento Beefree (biblioteca, e-mails do sistema,
  // blocos antigos): converte pelo importer antes de abrir. Sem isso o editor
  // abre em branco e parece que o corpo do e-mail sumiu.
  useEffect(() => {
    if (!existing || existing.beefreeJson.trim()) return;
    const source = templateSourceHtml(existing);
    if (!source.trim()) return;
    let active = true;
    setImportState({ status: 'loading' });
    void importHtmlDocument(source).then((result) => {
      if (!active) return;
      if ('document' in result) {
        const json = JSON.stringify(result.document);
        setBeefreeJson(json);
        latestJson.current = json;
        heldRef.current = { ...heldRef.current, beefreeJson: json };
        savedRef.current = { ...savedRef.current, beefreeJson: json };
        setImportState({ status: 'idle' });
      } else {
        setImportState({ status: 'error', message: result.message });
      }
    });
    return () => {
      active = false;
    };
  }, [bootKey, existing]);

  // Modelo novo criado antes das identidades chegarem: assume o domínio da loja quando ele aparece.
  useEffect(() => {
    if (!domain && defaultDomain) setDomain(defaultDomain);
  }, [defaultDomain, domain]);

  const domains = useMemo(() => {
    const values = [...identities.map((item) => item.domain), domain];
    return [...new Set(values.filter(Boolean))];
  }, [identities, domain]);

  const document = parseBeefreeDocument(beefreeJson);
  const sandboxWithoutDocument = existing?.origin === 'sandbox' && !existing.beefreeJson.trim();
  const saved = savedRef.current;
  const editorDirty =
    contentDirty || html !== heldRef.current.html || beefreeJson !== heldRef.current.beefreeJson;
  const metaDirty =
    name !== saved.name ||
    category !== saved.category ||
    subject !== saved.subject ||
    previewText !== saved.previewText ||
    tags.join('\0') !== saved.tags ||
    domain !== saved.domain ||
    storesKey(applyMode === 'selected' ? stores : []) !== saved.appliesTo;
  const unsaved = editorDirty || metaDirty;
  const inSyncWithLibrary = !unsaved && html === saved.html && beefreeJson === saved.beefreeJson;
  const saving = busy === 'content' || busy === 'advance' || busy === 'confirm';

  const rememberSaved = (next: { html: string; beefreeJson: string }) => {
    const trimmed = name.trim().slice(0, 50);
    savedRef.current = {
      name: trimmed,
      category,
      subject,
      previewText,
      tags: tags.join('\0'),
      domain,
      html: next.html,
      beefreeJson: next.beefreeJson,
      appliesTo: storesKey(applyMode === 'selected' ? stores : []),
    };
    if (trimmed !== name) setName(trimmed);
    setContentDirty(false);
    setPersisted(true);
  };

  const persist = async (next: { html: string; beefreeJson: string; editor: TemplateEditor }) => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error('Dê um nome ao modelo.');
      nameInputRef.current?.focus();
      return false;
    }
    const selectedStores = applyMode === 'selected' ? stores : [];
    if (platformScope && applyMode === 'selected' && selectedStores.length === 0) {
      toast.error('Selecione ao menos uma loja ou marque “Todas as lojas”.');
      return false;
    }
    // Padrão salvo "só para algumas lojas" vira variante nova: o padrão das outras não muda.
    const forkAsVariant =
      platformScope && applyMode === 'selected' && Boolean(existing) && !existing?.variantOf && !existing?.appliesTo?.length;
    const draft: TemplateDraft = {
      id: forkAsVariant ? undefined : persistedId.current,
      name: trimmed.slice(0, 50),
      category,
      description: '',
      subject,
      previewText,
      editor: next.editor,
      blocks,
      html: next.html,
      beefreeJson: next.beefreeJson,
      plainText,
      tags,
      domain,
      ...(platformScope
        ? { appliesTo: selectedStores, variantOf: forkAsVariant ? existing?.id : existing?.variantOf }
        : {}),
    };
    try {
      const savedTemplate = await saveTemplate(draft);
      persistedId.current = savedTemplate.template.id;
      rememberSaved(next);
      if (forkAsVariant) {
        toast.success(`Variante criada para ${selectedStores.length} loja${selectedStores.length > 1 ? 's' : ''}.`, {
          description: 'O padrão das outras lojas não mudou.',
        });
        navigate(paths.templateEdit(savedTemplate.template.id), { replace: true });
        return true;
      }
      toast.success(
        savedTemplate.forked ? 'Cópia salva como modelo da loja.' : live ? 'Modelo salvo na biblioteca da loja.' : 'Modelo salvo nesta sessão.',
        {
          description: savedTemplate.forked
            ? 'O original não foi alterado.'
            : live
              ? 'Já pode ser usado nas campanhas desta loja.'
              : 'A plataforma ainda guarda os modelos só nesta sessão.',
        },
      );
      return true;
    } catch (error) {
      const { message, description } = marketingErrorToast(error, 'Não foi possível salvar o modelo.');
      toast.error(message, { description });
      return false;
    }
  };

  const holdContent = (nextHtml: string, nextJson: string, fromSdk: boolean) => {
    setHtml(nextHtml);
    setBeefreeJson(nextJson);
    if (fromSdk || nextJson.trim()) setEditor('beefree');
    latestJson.current = nextJson;
    heldRef.current = { html: nextHtml, beefreeJson: nextJson };
    setContentDirty(false);
    setContentHeld(true);
  };

  const finishHeld = async (next: { html: string; beefreeJson: string; editor: TemplateEditor }, intent: SaveIntent, fromSdk: boolean) => {
    holdContent(next.html, next.beefreeJson, fromSdk);
    if (intent === 'confirm' || name.trim()) {
      await persist({ html: next.html, beefreeJson: next.beefreeJson, editor: next.editor });
    } else if (intent === 'content') {
      toast.success('Conteúdo salvo neste editor.', {
        description: 'O modelo entra na biblioteca quando você salvar os dados do e-mail.',
      });
    }
    if (intent === 'advance') setStep('details');
  };

  const deliver = async (payload: BeefreeSavePayload, intent: SaveIntent): Promise<void> => {
    if (intent === 'preview') {
      setHtml(payload.html);
      setBeefreeJson(payload.json);
      setEditor('beefree');
      latestJson.current = payload.json;
      setContentDirty(payload.json !== heldRef.current.beefreeJson || payload.html !== heldRef.current.html);
      setPreviewHtml(payload.html);
      setPreviewOpen(true);
      return;
    }
    await finishHeld({ html: payload.html, beefreeJson: payload.json, editor: 'beefree' }, intent, true);
  };

  const addTag = () => {
    const pieces = tagDraft
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
    if (pieces.length === 0) return;
    setTags((current) => {
      const next = [...current];
      for (const piece of pieces) {
        if (next.length >= MAX_TAGS) break;
        if (!next.includes(piece)) next.push(piece);
      }
      return next;
    });
    setTagDraft('');
  };

  const runSave = async (intent: Exclude<SaveIntent, 'preview'>) => {
    if (intent === 'confirm' && !name.trim()) {
      toast.error('Dê um nome ao modelo.');
      nameInputRef.current?.focus();
      return;
    }
    if (!(tokenState.status === 'ready' && saveBridge.current)) {
      const nextEditor: TemplateEditor = beefreeJson.trim() ? 'beefree' : editor;
      setBusy(intent);
      try {
        await finishHeld({ html, beefreeJson, editor: nextEditor }, intent, false);
      } finally {
        setBusy(null);
      }
      return;
    }
    setBusy(intent);
    delivered.current = false;
    armed.current = intent;
    blockUntil.current = Date.now() + 800;
    try {
      const payload = await saveBridge.current();
      if (!delivered.current) {
        if (!payload) {
          toast.error('O Beefree não devolveu o documento. Nada foi gravado.');
          return;
        }
        delivered.current = true;
        await deliver(payload, intent);
      }
    } catch {
      toast.error('Não foi possível salvar o conteúdo do Beefree.');
    } finally {
      setBusy(null);
    }
  };

  const openPreview = async () => {
    if (!(tokenState.status === 'ready' && saveBridge.current)) {
      setPreviewHtml(html);
      setPreviewOpen(true);
      return;
    }
    setBusy('preview');
    delivered.current = false;
    armed.current = 'preview';
    blockUntil.current = Date.now() + 800;
    try {
      const payload = await saveBridge.current();
      if (!delivered.current) {
        delivered.current = true;
        if (payload) {
          setHtml(payload.html);
          setBeefreeJson(payload.json);
          setEditor('beefree');
          latestJson.current = payload.json;
          setContentDirty(payload.json !== heldRef.current.beefreeJson || payload.html !== heldRef.current.html);
          setPreviewHtml(payload.html);
        } else {
          setPreviewHtml(html);
        }
        setPreviewOpen(true);
      }
    } catch {
      toast.error('Não foi possível atualizar a prévia. Mostrando o último conteúdo.');
      setPreviewHtml(html);
      setPreviewOpen(true);
    } finally {
      setBusy(null);
    }
  };

  const refreshToken = async () => {
    const result = await fetchBeefreeToken();
    if (!('token' in result)) return null;
    return result.token;
  };

  const sessionNote = sandboxWithoutDocument
    ? 'Este modelo veio em HTML. O Beefree abre com esse HTML convertido. A primeira gravação cria uma cópia da loja. O original do sandbox não é atualizado.'
    : existing?.origin === 'sandbox'
      ? 'A primeira gravação cria uma cópia da loja. O original do sandbox não é atualizado.'
      : live
        ? 'Salvar guarda o conteúdo neste editor. No passo dos dados, o modelo grava HTML, documento e metadados na biblioteca da loja. A cópia de envio só nasce na aprovação da campanha.'
        : 'Salvar guarda o conteúdo neste editor. No passo dos dados, o modelo grava HTML, documento e metadados só nesta sessão.';

  const statusLabel = saving
    ? 'Salvando…'
    : unsaved
      ? 'Alterações não salvas'
      : inSyncWithLibrary && persisted
        ? (live ? 'Salvo na biblioteca' : 'Salvo nesta sessão')
        : contentHeld
          ? 'Conteúdo salvo'
          : 'Não salvo';

  const editorTitle = name.trim() || (editing ? 'Editar modelo' : 'Novo template');
  const locked = busy !== null;

  if (templateId && !existing) {
    const pending = templatesState.phase === 'loading';
    const failed = templatesState.phase === 'error';
    const missingTitle = pending ? 'Carregando modelo…' : failed ? 'Não foi possível carregar o modelo' : 'Modelo não encontrado';
    return (
      <div className="flex h-full min-h-0 flex-1 flex-col">
        <header className="glass-thin flex h-14 shrink-0 items-center gap-2 px-2 sm:px-3">
          <Button variant="ghost" size="icon-sm" className="shrink-0" asChild>
            <Link to={paths.templates} aria-label="Voltar para modelos">
              <ChevronLeft />
            </Link>
          </Button>
          <h1 className="min-w-0 truncate text-base font-semibold">{missingTitle}</h1>
        </header>
        {pending ? (
          <div className="grid flex-1 place-items-center px-6 text-sm text-muted-foreground" aria-busy="true">
            Buscando o modelo na biblioteca…
          </div>
        ) : (
          <EmptyState
            icon={LayoutTemplate}
            title={missingTitle}
            description={
              failed
                ? templatesState.error?.message ?? 'O servidor não respondeu.'
                : 'Esse modelo não existe nesta loja ou foi excluído.'
            }
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden">
      <div className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <div
          className={cn(
            'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden',
            step === 'details' && 'pointer-events-none invisible',
          )}
          aria-hidden={step === 'details' ? true : undefined}
        >
          <header className="glass-thin flex h-14 min-w-0 shrink-0 items-center gap-2 overflow-x-auto overflow-y-hidden border-x-0 border-t-0 px-2 shadow-none sm:px-3">
            <Button variant="ghost" size="icon-sm" className="shrink-0" asChild>
              <Link to={paths.templates} aria-label="Voltar para modelos">
                <ChevronLeft />
              </Link>
            </Button>
            <h1 className="min-w-0 flex-1 truncate text-base font-semibold">{editorTitle}</h1>
            <p
              className={cn(
                'hidden shrink-0 truncate text-xs sm:block',
                unsaved ? 'text-warning' : 'text-muted-foreground',
              )}
              role="status"
              aria-live="polite"
            >
              {statusLabel}
            </p>
            <Tooltip content={sessionNote} side="bottom" align="start">
              <Button type="button" variant="ghost" size="icon-sm" className="shrink-0" aria-label="Como o salvamento funciona">
                <Info />
              </Button>
            </Tooltip>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="shrink-0"
              onClick={() => void openPreview()}
              loading={busy === 'preview'}
              disabled={locked && busy !== 'preview'}
            >
              <Eye />
              Pré-visualizar
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="shrink-0"
              onClick={() => void runSave('content')}
              loading={busy === 'content'}
              disabled={locked && busy !== 'content'}
            >
              Salvar
            </Button>
            <Button
              type="button"
              size="sm"
              className="shrink-0"
              onClick={() => void runSave('advance')}
              loading={busy === 'advance'}
              disabled={locked && busy !== 'advance'}
            >
              Salvar e avançar
            </Button>
          </header>

          <div className="relative min-h-0 min-w-0 flex-1">
            <div className="absolute inset-0">
              {tokenState.status === 'loading' || (tokenState.status === 'ready' && importState.status === 'loading') ? (
                <div className="grid h-full place-items-center px-6 text-sm text-muted-foreground">
                  {tokenState.status === 'loading' ? 'Abrindo o editor Beefree…' : 'Convertendo o HTML do modelo…'}
                </div>
              ) : null}
              {tokenState.status === 'ready' && importState.status === 'error' ? (
                <div className="grid h-full place-items-center px-6">
                  <div className="max-w-md text-center">
                    <h2 className="text-base font-medium">Conteúdo não carregado</h2>
                    <p className="mt-2 text-sm text-muted-foreground">{importState.message}</p>
                    <p className="mt-3 text-sm text-muted-foreground">
                      O HTML deste modelo continua guardado. O editor não foi aberto para não gravar um corpo vazio por cima.
                    </p>
                  </div>
                </div>
              ) : null}
              {tokenState.status === 'missing' || tokenState.status === 'error' ? (
                <div className="grid h-full place-items-center px-6">
                  <div className="max-w-md text-center">
                    <h2 className="text-base font-medium">Editor Beefree</h2>
                    <p className="mt-2 text-sm text-muted-foreground">{tokenState.message}</p>
                    <p className="mt-3 text-sm text-muted-foreground">
                      A página segue no ar. A biblioteca e os modelos desta sessão continuam disponíveis. O editor não foi aberto.
                    </p>
                  </div>
                </div>
              ) : null}
              {tokenState.status === 'ready' && importState.status === 'idle' ? (
                <BeefreeEditor
                  key={`${bootKey}-${editorEpoch}`}
                  uid={tokenState.uid}
                  token={tokenState.token}
                  document={document}
                  saveBridge={saveBridge}
                  onSave={(payload) => {
                    if (Date.now() < blockUntil.current) {
                      if (delivered.current) return;
                      delivered.current = true;
                      void deliver(payload, armed.current ?? 'content');
                      return;
                    }
                    void deliver(payload, 'content');
                  }}
                  onChangeJson={(json) => {
                    latestJson.current = json;
                    setContentDirty(json !== heldRef.current.beefreeJson);
                  }}
                  onRefreshToken={refreshToken}
                  onRemount={(json) => {
                    const next = json || latestJson.current;
                    if (next.trim()) setBeefreeJson(next);
                    setEditorEpoch((current) => current + 1);
                  }}
                />
              ) : null}
            </div>
          </div>
        </div>

        {step === 'details' ? (
          <div className="absolute inset-0 z-10 overflow-auto">
            <div className="mx-auto w-full max-w-2xl px-4 py-6 lg:px-8 lg:py-8">
              <PageHeader
                title="Dados do e-mail"
                description="O conteúdo do editor permanece ao voltar."
                leading={
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    className="-ml-2"
                    onClick={() => setStep('editor')}
                    aria-label="Voltar ao editor"
                  >
                    <ChevronLeft />
                  </Button>
                }
                actions={
                  <Button type="button" onClick={() => void runSave('confirm')} loading={busy === 'confirm'} disabled={locked && busy !== 'confirm'}>
                    Salvar
                  </Button>
                }
              />
              <EmailDetailsFields
                name={name}
                onName={setName}
                nameInputRef={nameInputRef}
                category={category}
                onCategory={setCategory}
                subject={subject}
                onSubject={setSubject}
                previewText={previewText}
                onPreviewText={setPreviewText}
                tags={tags}
                tagDraft={tagDraft}
                onTagDraft={setTagDraft}
                onAddTag={addTag}
                onRemoveTag={(tag) => setTags((current) => current.filter((item) => item !== tag))}
                domain={domain}
                domains={domains}
                onDomain={setDomain}
              />
              {platformScope ? (
                <ApplyToField
                  className="mt-4"
                  mode={applyMode}
                  onMode={setApplyMode}
                  stores={stores}
                  onStores={setStores}
                  lockedToSelected={isVariant}
                />
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent size="wide">
          <DialogHeader>
            <DialogTitle>Pré-visualização</DialogTitle>
            <DialogDescription>Conteúdo desta sessão, com dados de exemplo.</DialogDescription>
          </DialogHeader>
          <EmailPreview
            subject={subject}
            previewText={previewText}
            blocks={blocks}
            storeName={storeName}
            fromName={scope.sender?.name}
            fromEmail={scope.sender?.email ?? `novidades@${domain}`}
            editor={editor}
            html={previewHtml ?? html}
            samples={scope.sampleVariables}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
